import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import { useCanal } from '../lib/tiempoReal'
import { clasesDeColor, cuentaAtras, etiquetaDeEstado } from '../lib/estados'
import { puede } from '../lib/permisos'
import { CambiarEstado } from '../componentes/CambiarEstado'
import type { Catalogos, FichaEnLista, RespuestaFicha, Usuario } from '../lib/tipos'

const COLUMNAS = [
  'Código',
  'Estado',
  'Cliente',
  'Teléfono',
  'Compañía',
  'CUPS',
  'Tarifa',
  'Puntos',
  'Comercial',
  'Creado',
]

/** Las bajas tienen su propio apartado desde el 15 de octubre. */
const FUERA_DE_AQUI = ['BAJA']

/**
 * Llamadas y contratos, en un solo apartado.
 *
 * Eran dos pantallas y el documento de octubre las junta: «se fusionarán los
 * actuales apartados de Llamadas y Contratos en un único apartado denominado
 * Contratos». Lo que cambia según quién mire son las pestañas y los botones,
 * no la pantalla.
 *
 * El estado se cambia desde la propia lista, sin entrar en el contrato, que fue
 * lo que pidió José el 15 de octubre. Qué se puede hacer con cada uno no lo
 * decide esta pantalla: viene en `ficha.acciones`, del servidor, que es quien
 * tiene la matriz de transiciones.
 */
export function Contratos({ usuario, ir }: { usuario: Usuario; ir: (destino: string) => void }) {
  const clienteQuery = useQueryClient()
  const [busqueda, setBusqueda] = useState('')
  const [estado, setEstado] = useState('')
  const [subestado, setSubestado] = useState('')
  const [telefono, setTelefono] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  const lista = useQuery({
    queryKey: ['fichas', estado, subestado, busqueda],
    queryFn: () =>
      api<{ fichas: FichaEnLista[] }>(
        `/api/fichas?estado=${encodeURIComponent(estado)}` +
          `&subestado=${encodeURIComponent(subestado)}&busqueda=${encodeURIComponent(busqueda)}`,
      ),
  })

  const catalogos = useQuery({
    queryKey: ['catalogos'],
    queryFn: () => api<Catalogos>('/api/catalogos'),
  })

  useCanal('sala', () => void clienteQuery.invalidateQueries({ queryKey: ['fichas'] }))

  const nueva = useMutation({
    mutationFn: (telefonoOrigen: string) =>
      api<RespuestaFicha>('/api/fichas', { metodo: 'POST', cuerpo: { telefono_origen: telefonoOrigen } }),
    onSuccess: (respuesta) => {
      setTelefono('')
      setAviso(null)
      ir(`/fichas/${respuesta.ficha.uuid}`)
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido crear'),
  })

  const cambiaEstados = puede(usuario, 'contratos.cambiar_estado')
  const exporta = puede(usuario, 'contratos.exportar')

  const catalogo = catalogos.data?.estados ?? []
  const pestañas = catalogo.filter(
    (item) =>
      !FUERA_DE_AQUI.includes(item.codigo) && (item.visible_comercial === 1 || cambiaEstados),
  )
  const subestadosDeFiltro = catalogo.find((item) => item.codigo === estado)?.subestados ?? []

  const fichas = lista.data?.fichas ?? []

  // Excel abre un CSV sin rechistar si lleva el BOM delante.
  const exportar = () => {
    const filas = fichas.map((ficha) => [
      ficha.codigo,
      etiquetaDeEstado(ficha),
      ficha.cliente ?? '',
      ficha.telefono_origen,
      ficha.compania ?? '',
      ficha.cups ?? '',
      ficha.tarifa_codigo ?? '',
      ficha.puntos_computados ?? '',
      ficha.comercial,
      ficha.creado_en,
    ])

    const csv = [COLUMNAS, ...filas]
      .map((fila) => fila.map((celda) => `"${String(celda).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n')

    const enlace = document.createElement('a')
    enlace.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
    enlace.download = `contratos-${new Date().toISOString().slice(0, 10)}.csv`
    enlace.click()
    URL.revokeObjectURL(enlace.href)
  }

  return (
    <div>
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="mb-1 text-sm font-bold">Nueva llamada</h2>
        <p className="mb-3 text-xs text-slate-500">
          El teléfono de origen se anota siempre, aunque la llamada no llegue a nada.
        </p>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            nueva.mutate(telefono)
          }}
        >
          <input
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="Teléfono de origen"
            inputMode="tel"
            autoFocus
            className="w-52 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-marca-500 focus:ring-2 focus:ring-marca-100 dark:border-slate-700 dark:bg-slate-950"
          />
          <button
            type="submit"
            disabled={nueva.isPending}
            className="rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-50"
          >
            {nueva.isPending ? 'Abriendo…' : 'Empezar'}
          </button>
        </form>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Nombre del cliente, código, teléfono o CUPS"
          className="min-w-52 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-marca-500 dark:border-slate-700 dark:bg-slate-950"
        />
        {exporta && fichas.length > 0 && (
          <button
            onClick={exportar}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium transition hover:border-marca-500 dark:border-slate-700"
          >
            Exportar a Excel
          </button>
        )}
      </div>

      <nav className="mb-2 flex flex-wrap gap-1">
        <button
          onClick={() => {
            setEstado('')
            setSubestado('')
          }}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
            estado === ''
              ? 'bg-marca-700 text-white'
              : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
          }`}
        >
          Todos
        </button>
        {pestañas.map((item) => (
          <button
            key={item.codigo}
            onClick={() => {
              setEstado(item.codigo)
              setSubestado('')
            }}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              estado === item.codigo
                ? 'bg-marca-700 text-white'
                : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            {item.etiqueta}
          </button>
        ))}
      </nav>

      {/* Los subestados son condicionales: solo salen los del estado elegido. */}
      {subestadosDeFiltro.length > 0 && (
        <nav className="mb-3 flex flex-wrap gap-1">
          <button
            onClick={() => setSubestado('')}
            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
              subestado === ''
                ? 'bg-slate-700 text-white'
                : 'border border-slate-200 text-slate-500 dark:border-slate-700'
            }`}
          >
            Todos
          </button>
          {subestadosDeFiltro.map((sub) => (
            <button
              key={sub.id}
              onClick={() => setSubestado(sub.codigo)}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                subestado === sub.codigo
                  ? 'bg-slate-700 text-white'
                  : 'border border-slate-200 text-slate-500 dark:border-slate-700'
              }`}
            >
              {sub.etiqueta}
              {sub.compania && <span className="ml-1 opacity-60">{sub.compania}</span>}
            </button>
          ))}
        </nav>
      )}

      {aviso && (
        <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{aviso}</p>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {lista.isLoading && <p className="px-4 py-6 text-sm text-slate-400">Cargando…</p>}
        {fichas.length === 0 && !lista.isLoading && (
          <p className="px-4 py-6 text-sm text-slate-400">Nada por aquí.</p>
        )}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {fichas.map((ficha) => {
            const plazo = cuentaAtras(ficha.vence_el)

            return (
              // La fila entera abre el contrato: «que no haya que hacer click en el
              // nombre solo». Lo de dentro que no es texto corta el clic.
              <li
                key={ficha.uuid}
                onClick={() => ir(`/fichas/${ficha.uuid}`)}
                className="flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
              >
                {/* El código sigue siendo un botón, que a la fila no llega el
                    teclado y así se puede tabular hasta el contrato. */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    ir(`/fichas/${ficha.uuid}`)
                  }}
                  className="font-mono font-semibold hover:underline"
                >
                  {ficha.codigo}
                </button>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${clasesDeColor(ficha.estado_color)}`}>
                  {etiquetaDeEstado(ficha)}
                </span>
                {/* Pegado al estado y no al final de la fila: se mira el estado y
                    se cambia ahí mismo, sin recorrerla con la vista. */}
                {cambiaEstados && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <CambiarEstado
                      uuid={ficha.uuid}
                      acciones={ficha.acciones}
                      catalogo={catalogo}
                      alFallar={setAviso}
                    />
                  </span>
                )}
                {plazo && (
                  <span
                    title="Horas laborables, con los festivos de Sevilla"
                    className={`text-xs font-medium ${plazo.apura ? 'text-red-600' : 'text-slate-400'}`}
                  >
                    {plazo.texto}
                  </span>
                )}
                {ficha.cliente && <span className="font-medium">{ficha.cliente}</span>}
                <span className="text-slate-500">{ficha.telefono_origen}</span>
                {ficha.compania && <span className="text-xs text-slate-500">{ficha.compania}</span>}
                {ficha.cups && <span className="font-mono text-xs text-slate-400">{ficha.cups}</span>}
                {ficha.puntos_computados !== null && (
                  <span className="rounded-full bg-marca-50 px-2 py-0.5 text-xs font-semibold text-marca-700">
                    {ficha.puntos_computados} puntos
                  </span>
                )}
                <span className="text-xs text-slate-400">
                  {ficha.comercial_uuid === usuario.uuid ? 'tuya' : ficha.comercial}
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
